// src/components/CreateEntityModal.js
import React, { useState, useEffect } from 'react';
import { ISSUE_SEVERITY_OPTIONS } from '../constants';

/**
 * A reusable modal for creating Goals, Initiatives, Issues, and Action Items
 */
const CreateEntityModal = ({
  isOpen,
  onClose,
  entityType, // 'goal', 'initiative', 'issue', or 'action-item'
  clientId,
  apiService,
  onSuccess,
  mode = 'create',
  entityId,
  initialData,
  // Optional: pre-populate with a parent entity (goal for initiative, initiative for issue/action-item)
  parentEntityId,
  parentEntityType,
  // Optional: staff list for DRI selection
  staffList = [],
  // Optional: parent entity lists for linking
  goalsList = [],
  initiativesList = [],
  // Optional: list of clients for account selection (enables optional account dropdown)
  clientsList = [],
  // Optional: department ID for creating internal items (when no client is selected)
  departmentId = null,
  // Optional: department API service for creating internal items
  departmentApiService = null,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [deadline, setDeadline] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [selectedGoalId, setSelectedGoalId] = useState(parentEntityId || '');
  const [selectedInitiativeId, setSelectedInitiativeId] = useState(parentEntityId || '');
  // For optional account selection - empty string means internal (no account)
  const [selectedClientId, setSelectedClientId] = useState(clientId || '');
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // WS3b: similar entities suggested after a successful create. Empty array =>
  // no suggestions (or feature disabled). When non-empty, the modal switches
  // from the "create form" view to the "similar items found" view.
  const [similarEntities, setSimilarEntities] = useState([]);
  // WS3b merge: the just-created entity's id, kept around so the user can
  // fold it into a chosen similar item via /embedding/merge.
  const [createdEntityId, setCreatedEntityId] = useState(null);
  // Per-row "merging in flight" state so we disable the right Merge button
  // while the request is pending (and disable everything else to avoid
  // a second concurrent merge).
  const [mergingTargetId, setMergingTargetId] = useState(null);
  const [mergeError, setMergeError] = useState(null);
  const isEditMode = mode === 'edit';
  
  // Show account selector when clientsList is provided and we're in create mode
  const showAccountSelector = !isEditMode && clientsList.length > 0;

  // Reset form when modal opens/closes or entity type changes
  useEffect(() => {
    if (!isOpen) return;

    if (isEditMode && initialData) {
      setName(initialData.name || '');
      setDescription(initialData.description || '');
      setTargetDate(initialData.targetDate || initialData.target_date || '');
      setDeadline(initialData.deadline || '');
      setAssignedTo(
        initialData.assignedTo ||
          initialData.assigned_to ||
          initialData.assignee ||
          ''
      );
      setSeverity(initialData.severity || 'medium');
      setSelectedGoalId('');
      setSelectedInitiativeId('');
      setSelectedClientId(clientId || '');
    } else {
      setName('');
      setDescription('');
      setTargetDate('');
      setDeadline('');
      setAssignedTo('');
      setSeverity('medium');
      setSelectedGoalId(parentEntityType === 'goal' ? parentEntityId : '');
      setSelectedInitiativeId(parentEntityType === 'initiative' ? parentEntityId : '');
      setSelectedClientId(clientId || '');
    }

    setError(null);
    setSimilarEntities([]);
    setCreatedEntityId(null);
    setMergingTargetId(null);
    setMergeError(null);
  }, [isOpen, entityType, parentEntityId, parentEntityType, isEditMode, initialData, clientId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    // For internal items, we need either departmentId/departmentApiService or a selected client
    const isInternalItem = !selectedClientId && showAccountSelector;
    if (isInternalItem && !departmentApiService) {
      setError('Please select an account or ensure department context is available');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let result;
      const baseData = {
        name: name.trim(),
        description: description.trim() || undefined,
        targetDate: targetDate || undefined,
        assignedTo: assignedTo || undefined,
      };

      // Determine which client ID and API service to use
      const effectiveClientId = selectedClientId || clientId;
      const effectiveApiService = isInternalItem ? departmentApiService : apiService;

      if (isEditMode) {
        if (!entityId) {
          throw new Error('Missing entity ID for edit mode');
        }
        switch (entityType) {
          case 'goal':
            result = await apiService.updateGoal(clientId, entityId, baseData);
            break;
          case 'initiative':
            result = await apiService.updateInitiative(clientId, entityId, baseData);
            break;
          case 'issue':
            result = await apiService.updateIssue(clientId, entityId, {
              ...baseData,
              severity,
            });
            break;
          case 'action-item':
            result = await apiService.updateActionItem(clientId, entityId, {
              name: name.trim(),
              description: description.trim() || undefined,
              deadline: deadline || undefined,
              assignedTo: assignedTo || undefined,
            });
            break;
          default:
            throw new Error(`Unknown entity type: ${entityType}`);
        }
      } else {
        switch (entityType) {
          case 'goal':
            result = await effectiveApiService.createGoal(effectiveClientId, baseData);
            break;
          case 'initiative':
            result = await effectiveApiService.createInitiative(effectiveClientId, {
              ...baseData,
              goalId: selectedGoalId || undefined,
            });
            break;
          case 'issue':
            result = await effectiveApiService.createIssue(effectiveClientId, {
              ...baseData,
              severity,
              initiativeId: selectedInitiativeId || undefined,
            });
            break;
          case 'action-item':
            result = await effectiveApiService.createActionItem(effectiveClientId, {
              name: name.trim(),
              description: description.trim() || undefined,
              deadline: deadline || undefined,
              assignedTo: assignedTo || undefined,
            });
            // If an initiative was selected, link the action item to it
            if (result && result.id && selectedInitiativeId) {
              await apiService.linkActionItemToInitiative(effectiveClientId, result.id, selectedInitiativeId);
            }
            break;
          default:
            throw new Error(`Unknown entity type: ${entityType}`);
        }
      }

      if (onSuccess) {
        onSuccess(result);
      }

      // WS3b: if the backend returned similar entities, show the
      // "Similar Items Found" panel instead of closing immediately. Only on
      // create flow - update flow always closes.
      const suggestions = !isEditMode && result && Array.isArray(result.similarEntities)
        ? result.similarEntities
        : [];
      if (suggestions.length > 0) {
        setSimilarEntities(suggestions);
        setCreatedEntityId(result?.id ?? null);
      } else {
        onClose();
      }
    } catch (err) {
      console.error(`Error creating ${entityType}:`, err);
      setError(err.message || `Failed to create ${entityType}`);
    } finally {
      setIsLoading(false);
    }
  };

  const showingSimilarPanel = similarEntities.length > 0;
  const dismissSimilarPanel = () => {
    setSimilarEntities([]);
    setCreatedEntityId(null);
    setMergingTargetId(null);
    setMergeError(null);
    onClose();
  };

  // WS3b user-initiated merge: fold the just-created entity (createdEntityId)
  // into the chosen existing entity (targetId). On success, the existing
  // entity is the survivor and now carries any unique content from the new
  // entity. Closes the modal afterward and pings onSuccess so the caller can
  // refresh its list view.
  const handleMergeInto = async (targetId, targetName) => {
    if (!apiService || !createdEntityId || !targetId) return;
    if (mergingTargetId) return; // single in-flight at a time
    setMergeError(null);
    setMergingTargetId(targetId);
    try {
      const merged = await apiService.mergeEntities(
        entityType,
        createdEntityId,
        targetId,
        clientId,
      );
      // Tell the caller something landed so it can refresh its list. Caller
      // can distinguish a merge from a vanilla create by the presence of
      // merged.source_deleted_id.
      if (onSuccess) onSuccess(merged);
      // Close the modal entirely - the user has resolved the duplicate.
      dismissSimilarPanel();
    } catch (err) {
      console.error(`Merge into ${targetName} failed:`, err);
      setMergeError(
        err.message || `Failed to merge into "${targetName}". Try again.`,
      );
    } finally {
      setMergingTargetId(null);
    }
  };

  if (!isOpen) return null;

  const getEntityTypeDisplay = () => {
    switch (entityType) {
      case 'goal': return 'Goal';
      case 'initiative': return 'Initiative';
      case 'issue': return 'Issue';
      case 'action-item': return 'Action Item';
      default: return 'Entity';
    }
  };

  const headerText = isEditMode
    ? `Edit ${getEntityTypeDisplay()}`
    : `Create New ${getEntityTypeDisplay()}`;

  const getEntityColor = () => {
    switch (entityType) {
      case 'goal': return 'var(--pallium-primary-purple)';
      case 'initiative': return 'var(--pallium-info-cyan)';
      case 'issue': return 'var(--pallium-error-red)';
      case 'action-item': return 'var(--pallium-success-green)';
      default: return 'var(--pallium-primary-purple)';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div 
        className="rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-lg)' }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${getEntityColor()}15` }}
          >
            <svg 
              className="w-5 h-5"
              style={{ color: getEntityColor() }}
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-xl font-bold" style={{ color: 'var(--pallium-text-primary)' }}>
            {showingSimilarPanel ? 'Similar Items Found' : headerText}
          </h2>
        </div>

        {showingSimilarPanel && (
          <div>
            <div
              className="mb-4 p-3 rounded-lg"
              style={{
                backgroundColor: 'var(--pallium-success-green-bg, #f0fdf4)',
                border: '1px solid var(--pallium-success-green, #22c55e)',
                color: 'var(--pallium-success-green, #166534)',
              }}
            >
              <strong>{getEntityTypeDisplay()} created.</strong> We found {similarEntities.length}{' '}
              existing {similarEntities.length === 1 ? 'item' : 'items'} that look similar.
              Review them below to avoid duplicates, or dismiss to keep yours.
            </div>
            {mergeError && (
              <div
                className="mb-3 p-3 rounded-lg text-sm"
                style={{
                  backgroundColor: 'var(--pallium-error-red-bg, #fef2f2)',
                  border: '1px solid var(--pallium-error-red, #ef4444)',
                  color: 'var(--pallium-error-red, #991b1b)',
                }}
              >
                {mergeError}
              </div>
            )}
            <ul className="mb-4" style={{ listStyle: 'none', padding: 0 }}>
              {similarEntities.map((entity) => {
                const thisIsMerging = mergingTargetId === entity.id;
                const anyMergeInFlight = mergingTargetId !== null;
                return (
                  <li
                    key={entity.id}
                    className="mb-2 p-3 rounded-lg flex items-start justify-between gap-3"
                    style={{
                      border: '1px solid var(--pallium-stroke-light)',
                      backgroundColor: 'var(--pallium-bg-secondary, #f8fafc)',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        className="font-medium text-sm"
                        style={{ color: 'var(--pallium-text-primary)' }}
                      >
                        {entity.name}
                      </div>
                      {entity.description && (
                        <div
                          className="text-xs mt-1"
                          style={{ color: 'var(--pallium-text-tertiary)' }}
                        >
                          {entity.description.length > 140
                            ? `${entity.description.substring(0, 140)}…`
                            : entity.description}
                        </div>
                      )}
                      <div
                        className="text-xs mt-1"
                        style={{ color: 'var(--pallium-icon-gray)' }}
                      >
                        {entity.status ? `Status: ${entity.status} · ` : ''}
                        Similarity: {Math.round((entity.score || 0) * 100)}%
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button
                        type="button"
                        title={`Fold the ${getEntityTypeDisplay()} you just created into "${entity.name}". Source is deleted, target is enriched. This action is auditable.`}
                        onClick={() => handleMergeInto(entity.id, entity.name)}
                        disabled={anyMergeInFlight || !createdEntityId}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap"
                        style={{
                          backgroundColor: anyMergeInFlight
                            ? 'var(--pallium-bg-tertiary, #e5e7eb)'
                            : getEntityColor(),
                          color: 'white',
                          opacity: anyMergeInFlight && !thisIsMerging ? 0.6 : 1,
                          cursor: anyMergeInFlight ? 'wait' : 'pointer',
                        }}
                      >
                        {thisIsMerging ? 'Merging…' : 'Merge into this'}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={dismissSimilarPanel}
                disabled={mergingTargetId !== null}
                className="px-4 py-2 text-sm font-medium rounded-lg"
                style={{
                  backgroundColor: getEntityColor(),
                  color: 'white',
                  opacity: mergingTargetId !== null ? 0.6 : 1,
                  cursor: mergingTargetId !== null ? 'not-allowed' : 'pointer',
                }}
              >
                Keep both & close
              </button>
            </div>
          </div>
        )}

        {!showingSimilarPanel && (
        <form onSubmit={handleSubmit}>
          {/* Account selector (optional - only shown when clientsList is provided) */}
          {showAccountSelector && (
            <div className="mb-4">
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--pallium-text-secondary)' }}
              >
                Account (optional)
              </label>
              <select
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                style={{ 
                  border: '1px solid var(--pallium-stroke-light)',
                  backgroundColor: 'var(--pallium-white)',
                }}
              >
                <option value="">Internal (No Account){departmentId ? ` - ${departmentId}` : ''}</option>
                {clientsList.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
              <p className="text-xs mt-1" style={{ color: 'var(--pallium-text-tertiary)' }}>
                Leave blank for an internal {entityType}, or select an account to associate it with a client.
              </p>
            </div>
          )}

          {/* Name field */}
          <div className="mb-4">
            <label 
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--pallium-text-secondary)' }}
            >
              Name <span style={{ color: 'var(--pallium-error-red)' }}>*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`Enter ${entityType} name...`}
              className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
              style={{ 
                border: '1px solid var(--pallium-stroke-light)',
                backgroundColor: 'var(--pallium-white)',
              }}
              required
              autoFocus
            />
          </div>

          {/* Description field */}
          <div className="mb-4">
            <label 
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--pallium-text-secondary)' }}
            >
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description (optional)..."
              rows={3}
              className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2 resize-none"
              style={{ 
                border: '1px solid var(--pallium-stroke-light)',
                backgroundColor: 'var(--pallium-white)',
              }}
            />
          </div>

          {/* Target Date field (for goals and initiatives) */}
          {(entityType === 'goal' || entityType === 'initiative') && (
            <div className="mb-4">
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--pallium-text-secondary)' }}
              >
                Target Date
              </label>
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                style={{ 
                  border: '1px solid var(--pallium-stroke-light)',
                  backgroundColor: 'var(--pallium-white)',
                }}
              />
            </div>
          )}

          {/* Deadline field (for action items) */}
          {entityType === 'action-item' && (
            <div className="mb-4">
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--pallium-text-secondary)' }}
              >
                Deadline
              </label>
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                style={{ 
                  border: '1px solid var(--pallium-stroke-light)',
                  backgroundColor: 'var(--pallium-white)',
                }}
              />
            </div>
          )}

          {/* Severity field (for issues only) */}
          {entityType === 'issue' && (
            <div className="mb-4">
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--pallium-text-secondary)' }}
              >
                Severity
              </label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                style={{ 
                  border: '1px solid var(--pallium-stroke-light)',
                  backgroundColor: 'var(--pallium-white)',
                }}
              >
                {ISSUE_SEVERITY_OPTIONS.map(option => (
                  <option key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Link to Goal (for initiatives) */}
          {!isEditMode && entityType === 'initiative' && goalsList.length > 0 && (
            <div className="mb-4">
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--pallium-text-secondary)' }}
              >
                Link to Goal (optional)
              </label>
              <select
                value={selectedGoalId}
                onChange={(e) => setSelectedGoalId(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                style={{ 
                  border: '1px solid var(--pallium-stroke-light)',
                  backgroundColor: 'var(--pallium-white)',
                }}
              >
                <option value="">No goal selected</option>
                {goalsList.map(goal => (
                  <option key={goal.id} value={goal.id}>
                    {goal.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Link to Initiative (for issues and action items) */}
          {!isEditMode && (entityType === 'issue' || entityType === 'action-item') && initiativesList.length > 0 && (
            <div className="mb-4">
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--pallium-text-secondary)' }}
              >
                {entityType === 'issue' ? 'Blocks Initiative (optional)' : 'Supports Initiative (optional)'}
              </label>
              <select
                value={selectedInitiativeId}
                onChange={(e) => setSelectedInitiativeId(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                style={{ 
                  border: '1px solid var(--pallium-stroke-light)',
                  backgroundColor: 'var(--pallium-white)',
                }}
              >
                <option value="">No initiative selected</option>
                {initiativesList.map(initiative => (
                  <option key={initiative.id} value={initiative.id}>
                    {initiative.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Assigned To / DRI field */}
          {staffList.length > 0 && (
            <div className="mb-4">
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--pallium-text-secondary)' }}
              >
                Assigned To (DRI)
              </label>
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                style={{ 
                  border: '1px solid var(--pallium-stroke-light)',
                  backgroundColor: 'var(--pallium-white)',
                }}
              >
                <option value="">Unassigned (will be calculated)</option>
                {staffList.map(staff => (
                  <option key={staff} value={staff}>
                    {staff}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div 
              className="mb-4 p-3 rounded-lg text-sm"
              style={{ 
                backgroundColor: 'var(--pallium-alert-error-bg)', 
                color: 'var(--pallium-error-red)',
                border: '1px solid var(--pallium-error-red)'
              }}
            >
              {error}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
              style={{ 
                color: 'var(--pallium-text-secondary)',
                backgroundColor: 'var(--pallium-bg-secondary)',
              }}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium rounded-lg transition-colors text-white"
              style={{ 
                backgroundColor: isLoading ? 'var(--pallium-icon-gray)' : getEntityColor(),
              }}
              disabled={isLoading}
            >
              {isLoading
                ? (isEditMode ? 'Saving...' : 'Creating...')
                : (isEditMode ? `Save ${getEntityTypeDisplay()}` : `Create ${getEntityTypeDisplay()}`)}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default CreateEntityModal;
